import React, { ChangeEvent } from "react";
import { RouteComponentPropsHistory } from "../../types";

import withTitle from "../withTitle";

import { WellKnownGroups } from "../AuthContext";
import { V1, V2 } from "../../Api";
import DelayInput from "../DelayInput";
import { formatName, formatDateTimeLocal } from "../../Utilities";
import withProps from "../withProps";

interface IOutreachLeadListViewState {
    isLoading: boolean | string;

    searchText: string;
    searchOffset: number;
    searchPerson: string;

    leads: any[];
    employees: any[];
}

interface IOutreachLeadListViewProps extends RouteComponentPropsHistory<any, any> {
    group: string;
}

const PAGE_SIZE = 50;

class OutreachLeadListView extends React.Component<IOutreachLeadListViewProps, IOutreachLeadListViewState> {
    constructor(props: Readonly<IOutreachLeadListViewProps>) {
        super(props);

        const state: IOutreachLeadListViewState = {
            isLoading: true,

            searchText: "",
            searchOffset: 0,
            searchPerson: "",

            leads: [],
            employees: []
        };

        if (props.location.state) {
            const pState = props.location.state;

            state.searchText = pState.searchText || "";
            state.searchOffset = pState.searchOffset || 0;
            state.searchPerson = pState.searchPerson || "";
        }

        this.state = state;

        this.handleElementChange = this.handleElementChange.bind(this);
        this.loadData = this.loadData.bind(this);
        this.previousPage = this.previousPage.bind(this);
        this.nextPage = this.nextPage.bind(this);
    }

    public componentDidMount() {
        this.loadData();
    }

    public render() {
        return (
            <div className="viewer-root">
                <div className="filter-control-box">
                    <DelayInput type="search" timeout={500} className="fill-space" name="searchText" value={this.state.searchText} placeholder="Search..." onChangeDelayed={this.handleElementChange} />
                    <select name="searchPerson" value={this.state.searchPerson} onChange={this.handleElementChange}>
                        <option value="">Everyone</option>
                        {this.state.employees.map(a => <option key={a.id} value={a.id}>{formatName(a.name)}</option>)}
                    </select>
                </div>
                <table className="table-vertical">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Date Added</th>
                            <th>Date Contacted</th>
                            <th>Added By</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <thead className="table-vertical-head-small">
                        <tr>
                            <th>Outreach Leads</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            this.state.isLoading === true
                                ? <tr><td colSpan={10}><div className="spinner spinner-center lds-dual-ring" /></td></tr>
                                : typeof this.state.isLoading === "string"
                                    ? <tr><td colSpan={10}><span className="error-text">{this.state.isLoading}</span></td></tr>
                                    : this.state.leads.length === 0
                                        ? <tr><td colSpan={10}>No results found</td></tr>
                                        : this.state.leads.map((a, i) => (
                                            <tr className="clickable" key={a.id} tabIndex={0} onClick={() => this.goSurvey(a.id)}>
                                                <td className="inline" data-caption="Name: ">{formatName(a.name)}</td>
                                                <td data-caption="Date Added: ">{formatDateTimeLocal(a.creationTimestamp)}</td>
                                                <td className="inline" data-caption="Date Contacted: ">{formatDateTimeLocal(a.contactTimestamp) || "-"}</td>
                                                <td className="inline" data-caption="Added By: ">{formatName(a.creator?.name) || "-"}</td>
                                                <td className="inline" data-caption="Status: ">{this.statusToText(a.status)}</td>
                                            </tr>
                                        ))}
                    </tbody>
                </table>
                &nbsp;
                <div className="action-panel action-panel-noflip">
                    <button className="button push-right text-with-icon" onClick={this.previousPage} disabled={this.state.searchOffset === 0}><i className="icon-angle-left" /><span>Previous</span></button>
                    <button className="button text-with-icon" onClick={this.nextPage} disabled={this.state.leads.length < PAGE_SIZE}><span>Next</span><i className="icon-angle-right" /></button>
                </div>
            </div>
        );
    }

    private handleElementChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            searchOffset: 0,

            [name]: value
        } as any, () => this.loadData());
    }

    private async loadData() {
        try {
            this.setState({
                isLoading: true
            });

            if (this.state.employees.length === 0) {
                // Load employee list for this group
                const employees = await V1.get<any[]>(`employees/summary?group=${WellKnownGroups.PatientCareRepLeadInput}`);
                this.setState({
                    employees: employees.sort((a, b) => formatName(a.name).localeCompare(formatName(b.name)))
                });
            }

            this.saveSearchState();

            const parameters = [
                `offset=${this.state.searchOffset}`,
                `limit=${PAGE_SIZE}`,
            ];

            if (this.state.searchText) {
                parameters.push(`q=${encodeURIComponent(this.state.searchText)}`);
            }
            if (this.state.searchPerson) {
                parameters.push(`employeeId=${this.state.searchPerson}`);
            }

            const leads = await V2.get<any[]>(`survey/outreach/group/${this.props.group}/leads?${parameters.join("&")}`);

            this.setState({
                isLoading: false,

                leads,
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not load statements"
            });
        }
    }

    private saveSearchState() {
        const currentLocation = this.props.location;
        currentLocation.state = {
            searchOffset: this.state.searchOffset,
            searchText: this.state.searchText,
            searchPerson: this.state.searchPerson,
        };
        this.props.history.replace(currentLocation);
    }

    private nextPage() {
        this.setState(s => {
            return {
                searchOffset: s.searchOffset + PAGE_SIZE
            };
        }, () => this.loadData());
    }

    private previousPage() {
        this.setState(s => {
            return {
                searchOffset: Math.max(s.searchOffset - PAGE_SIZE, 0)
            };
        }, () => this.loadData());
    }

    private goSurvey(id: number) {
        this.props.history.push(`${this.props.location.pathname}/${id}`);
    }

    private statusToText(status: number) {
        switch (status) {
            case 1: return "Did not reach";
            case 2: return "Did not reach twice";
            case 3: return "Check back later";
            case 4: return "Not Needed";
            case 10: return "Reached, didn't want anything";
            case 11: return "Reached, made appointment";
            case 12: return "Do not contact";
            case 13: return "Made appointment afterwards";
            case undefined:
            case null: return "Not Contacted";
            default: return "Unknown status " + status;
        }
    }
}

export default withTitle("Outreach Leads", withProps(OutreachLeadListView, { group: WellKnownGroups.PatientCareRep }));
export const OutreachLeadListViewSurgicalConsultant = withTitle("Outreach Leads", withProps(OutreachLeadListView, { group: WellKnownGroups.SurgicalConsultant }));